import { getStrapiMediaAttributes } from './strapi';

const DEFAULT_SITE_NAME = import.meta.env.VITE_APP_NAME;
const DEFAULT_ROBOTS = 'index, follow';
const DEFAULT_SEO_AUTHOR = import.meta.env.VITE_SEO_AUTHOR;
const DEFAULT_SEO_PUBLISHER = import.meta.env.VITE_SEO_PUBLISHER;
const HOME_SLUGS = new Set(['', '/', 'home', 'homeeng', 'inicio']);

// Slugs que SIEMPRE deben rendir con noindex,follow (páginas legales).
// Mantener en lowercase.
const NOINDEX_SLUGS = new Set(['aviso-privacidad', 'terminos-condiciones', 'privacy-notice', 'terms-conditions']);

// Fallback estático para og:image cuando ninguna página trae seo.metaImage.
// Debe existir en /public con dimensiones 1200x630.
const DEFAULT_OG_IMAGE = {
  src: '/og-default.png',
  mime: 'image/png',
  width: 1200,
  height: 630,
};

const OG_LOCALE_MAP = {
  'es-mx': 'es_MX',
  en: 'en_US',
};

const trimText = (value) => (
  typeof value === 'string' ? value.trim() : ''
);

const SEO_AUTHOR = trimText(import.meta.env.VITE_SEO_AUTHOR) || DEFAULT_SEO_AUTHOR;
const SEO_PUBLISHER = trimText(import.meta.env.VITE_SEO_PUBLISHER) || DEFAULT_SEO_PUBLISHER;
const SITE_URL = trimText(import.meta.env.VITE_SITE_URL).replace(/\/+$/, '');

const normalizePath = (pathname = '/') => {
  if (!pathname || pathname === '/') return '/';

  const cleanedPath = String(pathname).replace(/^\/+|\/+$/g, '');
  return cleanedPath ? `/${cleanedPath}` : '/';
};

const isHomeSlug = (slug) => HOME_SLUGS.has(trimText(slug).toLowerCase());

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(trimText(value));

const getMediaField = (media, field) => {
  if (!media || typeof media === 'string') return null;

  return media[field] ?? media?.data?.[field] ?? media?.data?.attributes?.[field] ?? null;
};

const getMediaNumber = (media, field) => {
  const value = getMediaField(media, field);

  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const resolveOrigin = () => {
  if (SITE_URL) return SITE_URL;
  return typeof window !== 'undefined' ? window.location.origin : '';
};

const resolveAbsoluteUrl = (value, origin = resolveOrigin()) => {
  const trimmedValue = trimText(value);
  if (!trimmedValue) return null;

  if (isAbsoluteUrl(trimmedValue)) return trimmedValue;
  if (!origin) return trimmedValue;

  if (trimmedValue.startsWith('/')) {
    return `${origin}${trimmedValue}`;
  }

  return `${origin}/${trimmedValue.replace(/^\/+/, '')}`;
};

const getContactValue = (companyInfo, types) => {
  const contactItems = Array.isArray(companyInfo?.contactInfo) ? companyInfo.contactInfo : [];
  const match = contactItems.find((item) => types.includes(item?.type));

  return trimText(match?.value);
};

const getSocialLinks = (companyInfo) => {
  if (!Array.isArray(companyInfo?.socialMedia)) return [];

  return companyInfo.socialMedia
    .map((item) => trimText(item?.link || item?.url))
    .filter(Boolean);
};

const removeHeadTags = (selector) => {
  if (typeof document === 'undefined') return;

  document.head.querySelectorAll(selector).forEach((element) => element.remove());
};

const upsertHeadElement = (selector, tagName, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement(tagName);
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value == null || value === '') {
      element.removeAttribute(key);
      return;
    }

    element.setAttribute(key, String(value));
  });

  element.setAttribute('data-koppi-seo', 'true');

  document.head.querySelectorAll(selector).forEach((duplicate) => {
    if (duplicate !== element) duplicate.remove();
  });

  return element;
};

const setMetaTag = (selector, attributes, content) => {
  const trimmedContent = trimText(content);

  if (!trimmedContent) {
    removeHeadTags(selector);
    return;
  }

  const element = upsertHeadElement(selector, 'meta', attributes);
  element.setAttribute('content', trimmedContent);
};

const setLinkTag = (selector, attributes) => {
  const href = trimText(attributes?.href);

  if (!href) {
    removeHeadTags(selector);
    return;
  }

  upsertHeadElement(selector, 'link', attributes);
};

const setJsonLdScript = (scriptId, data) => {
  const selector = `script[data-koppi-seo-id="${scriptId}"]`;

  if (!data) {
    removeHeadTags(selector);
    return;
  }

  const element = upsertHeadElement(selector, 'script', {
    type: 'application/ld+json',
    'data-koppi-seo-id': scriptId,
  });

  element.textContent = JSON.stringify(data);
};

const toOgLocale = (locale) => {
  const normalizedLocale = trimText(locale).toLowerCase();
  return OG_LOCALE_MAP[normalizedLocale] || 'es_MX';
};

const resolveCanonicalUrl = ({ canonicalURL, origin, pageSlug, pathname }) => {
  const explicitCanonical = trimText(canonicalURL);
  if (explicitCanonical) return resolveAbsoluteUrl(explicitCanonical, origin);

  if (!origin) return null;

  const canonicalPath = isHomeSlug(pageSlug) ? normalizePath(pageSlug) : normalizePath(pathname);
  return `${origin}${canonicalPath === '/' ? '' : canonicalPath}`;
};

const resolveSeoImage = ({ seo, fallbackText, origin }) => {
  const metaImage = getStrapiMediaAttributes(seo?.metaImage, {
    altFallback: fallbackText,
    titleFallback: fallbackText,
  });

  if (metaImage.src) {
    return {
      ...metaImage,
      width: getMediaNumber(seo?.metaImage, 'width'),
      height: getMediaNumber(seo?.metaImage, 'height'),
      mime: trimText(getMediaField(seo?.metaImage, 'mime')) || undefined,
    };
  }

  // Fallback estático (apto para scrapers sociales): /og-default.png en public.
  return {
    ...DEFAULT_OG_IMAGE,
    src: resolveAbsoluteUrl(DEFAULT_OG_IMAGE.src, origin) || DEFAULT_OG_IMAGE.src,
    alt: fallbackText,
  };
};

const buildStructuredData = ({
  companyInfo,
  canonicalUrl,
  description,
  imageUrl,
  locale,
  pageSlug,
  publisherName,
  publisherUrl,
  title,
}) => {
  const companyName = trimText(companyInfo?.companyName) || DEFAULT_SITE_NAME;
  const sameAs = getSocialLinks(companyInfo);
  const phone = getContactValue(companyInfo, ['phone', 'whatsapp']);
  const email = getContactValue(companyInfo, ['email']);

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': isHomeSlug(pageSlug) ? 'WebSite' : 'WebPage',
    name: title,
    description,
    url: canonicalUrl,
    inLanguage: trimText(locale) || 'es-MX',
    publisher: {
      '@type': 'Organization',
      name: publisherName,
      ...(publisherUrl ? { url: publisherUrl } : {}),
    },
  };

  const realEstateSchema = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    name: companyName,
    description,
    url: canonicalUrl,
    ...(imageUrl ? { logo: imageUrl, image: imageUrl } : {}),
    ...(phone ? { telephone: phone } : {}),
    ...(email ? { email } : {}),
    ...(sameAs.length > 0 ? { sameAs } : {}),
  };

  return [webpageSchema, realEstateSchema];
};

export const applySeoMetadata = ({
  companyInfo,
  locale,
  noIndex = false,
  page,
  requestedSlug,
  seo,
}) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const pageSeo = seo || page?.seo || {};
  const pathname = normalizePath(window.location.pathname);
  const resolvedPageSlug = trimText(page?.slug);
  const pageSlug = resolvedPageSlug || trimText(requestedSlug);
  const siteName = trimText(companyInfo?.companyName) || DEFAULT_SITE_NAME;
  const title = trimText(pageSeo?.metaTitle)
    || trimText(page?.titulo)
    || trimText(page?.title)
    || siteName;
  const description = trimText(pageSeo?.metaDescription)
    || trimText(page?.descripcion)
    || trimText(page?.description)
    || siteName;
  const keywords = trimText(pageSeo?.keywords);
  const origin = resolveOrigin();
  const canonicalUrl = resolveCanonicalUrl({
    canonicalURL: pageSeo?.canonicalURL,
    origin,
    pageSlug,
    pathname,
  });
  const image = resolveSeoImage({
    seo: pageSeo,
    fallbackText: title,
    origin,
  });
  const trimmedRequestedSlug = trimText(requestedSlug);
  // Dos slugs distintos que ambos representan la home (p. ej. "home" vs "inicio")
  // NO se consideran alias: es la misma página por configuración i18n.
  const bothAreHomeSlugs = isHomeSlug(trimmedRequestedSlug) && isHomeSlug(resolvedPageSlug);
  const isAliasToDifferentPage = Boolean(
    trimmedRequestedSlug
    && resolvedPageSlug
    && trimmedRequestedSlug !== resolvedPageSlug
    && !bothAreHomeSlugs
  );
  const isHomeAlias = isHomeSlug(pageSlug)
    && pathname !== '/'
    && pathname !== normalizePath(pageSlug);
  // Slugs forzados a noindex (páginas legales, etc.).
  const isForcedNoIndex = NOINDEX_SLUGS.has(trimText(pageSlug).toLowerCase())
    || NOINDEX_SLUGS.has(trimmedRequestedSlug.toLowerCase());
  const robots = noIndex || isAliasToDifferentPage || isHomeAlias || isForcedNoIndex
    ? 'noindex, follow'
    : DEFAULT_ROBOTS;
  const ogLocale = toOgLocale(locale);
  const seoAuthor = resolveAbsoluteUrl(SEO_AUTHOR, origin) || SEO_AUTHOR;
  const seoPublisher = SEO_PUBLISHER;
  const twitterCard = image?.src ? 'summary_large_image' : 'summary';

  document.documentElement.lang = trimText(locale) || 'es-MX';
  document.title = title;

  setMetaTag('meta[name="description"]', { name: 'description' }, description);
  setMetaTag('meta[name="keywords"]', { name: 'keywords' }, keywords);
  setMetaTag('meta[name="robots"]', { name: 'robots' }, robots);
  setMetaTag('meta[name="author"]', { name: 'author' }, seoAuthor);
  setMetaTag('meta[name="publisher"]', { name: 'publisher' }, seoPublisher);

  setLinkTag('link[rel="canonical"]', {
    rel: 'canonical',
    href: canonicalUrl,
  });

  setMetaTag('meta[property="og:type"]', { property: 'og:type' }, 'website');
  setMetaTag('meta[property="og:title"]', { property: 'og:title' }, title);
  setMetaTag('meta[property="og:description"]', { property: 'og:description' }, description);
  setMetaTag('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
  setMetaTag('meta[property="og:site_name"]', { property: 'og:site_name' }, siteName);
  setMetaTag('meta[property="og:locale"]', { property: 'og:locale' }, ogLocale);
  setMetaTag('meta[property="og:image"]', { property: 'og:image' }, image?.src);
  setMetaTag('meta[property="og:image:alt"]', { property: 'og:image:alt' }, image?.alt || title);
  setMetaTag('meta[property="og:image:type"]', { property: 'og:image:type' }, image?.mime);
  setMetaTag('meta[property="og:image:width"]', { property: 'og:image:width' }, image?.width ? String(image.width) : '');
  setMetaTag('meta[property="og:image:height"]', { property: 'og:image:height' }, image?.height ? String(image.height) : '');

  setMetaTag('meta[name="twitter:card"]', { name: 'twitter:card' }, twitterCard);
  setMetaTag('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
  setMetaTag('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
  setMetaTag('meta[name="twitter:image"]', { name: 'twitter:image' }, image?.src);
  setMetaTag('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt' }, image?.alt || title);

  setJsonLdScript('core', buildStructuredData({
    companyInfo,
    canonicalUrl,
    description,
    imageUrl: image?.src || null,
    locale,
    pageSlug,
    publisherName: seoPublisher,
    publisherUrl: seoAuthor,
    title,
  }));
};
